import env from 'env';
import { ObjectValues } from 'types';
import mixpanel from 'mixpanel-browser';
import utils from 'utils';
import {
  LogoutProps,
  ErrorViewedProps,
  PageViewedProps,
  DocumentEventProps,
  MixpanelEventProps,
  SearchInitiatedEventProps,
  SecureLinkCreationInitiatedEventProps,
  ListingCreationInitiatedEventProps,
  ListingVisitedEventProps,
  MobileVerificationInitiatedEventProps,
  EnquiryCreationInitiatedEventProps,
  EnquiryWithdrawalInitiatedEventProps,
  SignupInitiatedProps,
  DealCreationInitiatedEventProps,
  SoftSignalInitiatedEventProps,
  SoftSignalSeenEventProps,
  DocumentPublishedEventProps,
  QuestionSubmissionInitiatedEventProps,
  QuestionReplyInitiatedEventProps,
  AnswerReviewInitiatedEventProps,
  QuestionReviewInitiatedEventProps,
  PhoneNumberSetupInitiatedEventProps,
  DataRoomTourTakenEventProps,
  DataRoomTourCardViewedEventProps,
  DataRoomTourHiddenForeverEventProps,
  DataRoomTourEnlargedEventProps,
  PasswordForgottenProps,
  ListingShareInitiatedEventProps,
  DesktopAppDownloadedEventProps,
  FileSecurityVisitedEventProps,
  MaxSecurityCardClickedEventProps,
  MaxSecurityInterestRegisteredEventProps,
  GroupFeaturesDiscoveredEventProps,
  MemberActivityFilterUpdatedEventProps,
  EnterpriseReportPopupOpenedEventProps,
  EnterpriseReportGenerateClickedEventProps,
  ChatAttachmentToastEventProps,
  ListingDocumentUploadedEventProps,
  AiDraftedFieldEditedEventProps,
  AutomationStageOpenedEventProps,
  AutomationTourStartedEventProps,
  AutomationTourStepViewedEventProps,
  AutomationTourCompletedEventProps,
  AutomationTourClosedEventProps,
  PausedLeadUnlockedEventProps,
  PausedLeadReplyOpenedEventProps,
  AutomationRemovalCancelledEventProps,
  DrawerRowNavigatedEventProps,
} from 'models/MixpanelEvents';

const MIXPANEL_EVENT = {
  LOG_OUT: 'Log Out',
  ERROR_VIEWED: 'Error Viewed',
  PAGE_VIEWED: 'Page Viewed',
  DOCUMENT_VIEWED: 'Document Viewed',
  SEARCH_INITIATED: 'Search Initiated',
  SECURE_LINK_CREATION_INITIATED: 'Secure Link Creation Initiated',
  LISTING_CREATION_INITIATED: 'Listing Creation Initiated',
  LISTING_VISITED: 'Listing Visited',
  MOBILE_VERIFICATION_INITIATED: 'Mobile Verification Initiated',
  ENQUIRY_CREATION_INITIATED: 'Enquiry Creation Initiated',
  ENQUIRY_WITHDRAWAL_INITIATED: 'Enquiry Withdrawal Initiated',
  SIGNUP_INITIATED: 'Signup Initiated',
  DEAL_CREATION_INITIATED: 'Deal Creation Initiated',
  SOFT_SIGNAL_INITIATED: 'Soft Signal Initiated',
  SOFT_SIGNAL_SEEN: 'Soft Signal Seen',
  DOCUMENT_PUBLISHED: 'Document Published Initiated',
  QUESTION_SUBMISSION_INITIATED: 'Question Submission Initiated',
  QUESTION_REPLY_INITIATED: 'Question Reply Initiated',
  ANSWER_REVIEW_INITIATED: 'Answer Review Initiated',
  QUESTION_REVIEW_INITIATED: 'Question Review Initiated',
  PHONE_NUMBER_SETUP_INITIATED: 'Phone Number Setup Initiated',
  DATA_ROOM_TOUR_TAKEN: 'Data Room Tour Taken',
  DATA_ROOM_TOUR_CARD_VIEWED: 'Data Room Tour Card Viewed',
  DATA_ROOM_TOUR_HIDDEN_FOREVER: 'Data Room Tour Hidden Forever',
  DATA_ROOM_TOUR_ENLARGED: 'Data Room Tour Enlarged',
  PASSWORD_FORGOTTEN_INITIATED: 'Password Forgotten Initiated',
  LISTING_SHARE_INITIATED: 'Listing Share Initiated',
  DESKTOP_APP_DOWNLOADED: 'Desktop App Downloaded',
  FILE_SECURITY_VISITED: 'File Security Visited',
  MAX_SECURITY_CARD_CLICKED: 'Max Security Card Clicked',
  MAX_SECURITY_INTEREST_REGISTERED: 'Max Security Interest Registered',
  GROUP_FEATURES_DISCOVERED: 'Group Features Discovered',
  MEMBER_ACTIVITY_FILTER_UPDATED: 'Member Activity Filter Updated',
  ENTERPRISE_REPORT_POPUP_OPENED: 'Enterprise report popup opened',
  ENTERPRISE_REPORT_GENERATE_CLICKED: 'Enterprise report generate clicked',
  CHAT_ATTACHMENT_TOAST_SHOWN: 'Chat Attachment Toast Shown',
  CHAT_ATTACHMENT_TOAST_SAVE_CLICKED: 'Chat Attachment Toast Save Clicked',
  CHAT_ATTACHMENT_MANAGE_ACCESS_CLICKED:
    'Chat Attachment Manage Access Clicked',
  LISTING_DOCUMENT_UPLOADED: 'Listing Document Uploaded',
  AI_DRAFTED_FIELD_EDITED: 'AI Drafted Field Edited',
  AUTOMATION_STAGE_OPENED: 'Automation Stage Opened',
  AUTOMATION_TOUR_STARTED: 'Automation Tour Started',
  AUTOMATION_TOUR_STEP_VIEWED: 'Automation Tour Step Viewed',
  AUTOMATION_TOUR_COMPLETED: 'Automation Tour Completed',
  AUTOMATION_TOUR_CLOSED: 'Automation Tour Closed',
  PAUSED_LEAD_UNLOCKED: 'Paused Lead Unlocked',
  PAUSED_LEAD_REPLY_OPENED: 'Paused Lead Reply Opened',
  AUTOMATION_REMOVAL_CANCELLED: 'Automation Removal Cancelled',
  DRAWER_ROW_NAVIGATED: 'Drawer Row Navigated',
} as const;

type MixpanelEvents = ObjectValues<typeof MIXPANEL_EVENT>;

const mixpanelToken = env.MIXPANEL_TOKEN;

export default {
  isInitialized: false,

  init() {
    if (!mixpanelToken || utils.isSSG()) return;

    mixpanel.init(mixpanelToken, {
      api_host: 'https://api-eu.mixpanel.com',
      record_sessions_percent: env.IS_PROD ? 100 : 0, // records 100% of all sessions on prod env
    });
    this.isInitialized = true;
  },

  getIsTrackingEnabled() {
    return this.isInitialized && !utils.isSSG();
  },

  identify(userId: string) {
    if (this.getIsTrackingEnabled()) {
      mixpanel.identify(userId);
    }
  },

  reset() {
    if (this.getIsTrackingEnabled()) {
      mixpanel.reset();
    }
  },

  trackEvent(eventName: MixpanelEvents, eventProps?: MixpanelEventProps) {
    if (this.getIsTrackingEnabled()) {
      try {
        mixpanel.track(eventName, eventProps);
      } catch {
        return;
      }
    }
  },

  logout(props: LogoutProps) {
    this.trackEvent(MIXPANEL_EVENT.LOG_OUT, props);
  },

  errorViewed(props: ErrorViewedProps) {
    this.trackEvent(MIXPANEL_EVENT.ERROR_VIEWED, props);
  },

  pageViewed(props: PageViewedProps) {
    this.trackEvent(MIXPANEL_EVENT.PAGE_VIEWED, props);
  },

  documentViewed(props: DocumentEventProps) {
    this.trackEvent(MIXPANEL_EVENT.DOCUMENT_VIEWED, props);
  },

  searchInitiated(props: SearchInitiatedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.SEARCH_INITIATED, props);
  },

  secureLinkCreationInitiated(props: SecureLinkCreationInitiatedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.SECURE_LINK_CREATION_INITIATED, props);
  },

  listingCreationInitiated(props: ListingCreationInitiatedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.LISTING_CREATION_INITIATED, props);
  },

  listingVisited(props: ListingVisitedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.LISTING_VISITED, props);
  },

  mobileVerificationInitiated(props: MobileVerificationInitiatedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.MOBILE_VERIFICATION_INITIATED, props);
  },

  enquiryCreationInitiated(props: EnquiryCreationInitiatedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.ENQUIRY_CREATION_INITIATED, props);
  },

  enquiryWithdrawalInitiated(props: EnquiryWithdrawalInitiatedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.ENQUIRY_WITHDRAWAL_INITIATED, props);
  },

  signupInitiated(props: SignupInitiatedProps) {
    this.trackEvent(MIXPANEL_EVENT.SIGNUP_INITIATED, props);
  },

  dealRoomCreationInitiated(props: DealCreationInitiatedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.DEAL_CREATION_INITIATED, props);
  },

  softSignalInitiated(props: SoftSignalInitiatedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.SOFT_SIGNAL_INITIATED, props);
  },

  softSignalSeen(props: SoftSignalSeenEventProps) {
    this.trackEvent(MIXPANEL_EVENT.SOFT_SIGNAL_SEEN, props);
  },

  documentPublished(props: DocumentPublishedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.DOCUMENT_PUBLISHED, props);
  },

  questionSubmissionInitiated(props: QuestionSubmissionInitiatedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.QUESTION_SUBMISSION_INITIATED, props);
  },

  questionReplyInitiated(props: QuestionReplyInitiatedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.QUESTION_REPLY_INITIATED, props);
  },

  answerReviewInitiated(props: AnswerReviewInitiatedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.ANSWER_REVIEW_INITIATED, props);
  },

  questionReviewInitiated(props: QuestionReviewInitiatedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.QUESTION_REVIEW_INITIATED, props);
  },

  phoneNumberSetupInitiated(props: PhoneNumberSetupInitiatedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.PHONE_NUMBER_SETUP_INITIATED, props);
  },

  dataRoomTourTaken(props: DataRoomTourTakenEventProps) {
    this.trackEvent(MIXPANEL_EVENT.DATA_ROOM_TOUR_TAKEN, props);
  },

  dataRoomTourCardViewed(props: DataRoomTourCardViewedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.DATA_ROOM_TOUR_CARD_VIEWED, props);
  },

  dataRoomTourHiddenForever(props: DataRoomTourHiddenForeverEventProps) {
    this.trackEvent(MIXPANEL_EVENT.DATA_ROOM_TOUR_HIDDEN_FOREVER, props);
  },

  dataRoomTourEnlarged(props: DataRoomTourEnlargedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.DATA_ROOM_TOUR_ENLARGED, props);
  },

  passwordForgottenInitiated(props: PasswordForgottenProps) {
    this.trackEvent(MIXPANEL_EVENT.PASSWORD_FORGOTTEN_INITIATED, props);
  },

  listingShareInitiated(props: ListingShareInitiatedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.LISTING_SHARE_INITIATED, props);
  },

  desktopAppDownloaded(props: DesktopAppDownloadedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.DESKTOP_APP_DOWNLOADED, props);
  },

  fileSecurityVisited(props: FileSecurityVisitedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.FILE_SECURITY_VISITED, props);
  },

  maxSecurityCardClicked(props: MaxSecurityCardClickedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.MAX_SECURITY_CARD_CLICKED, props);
  },

  maxSecurityInterestRegistered(
    props: MaxSecurityInterestRegisteredEventProps,
  ) {
    this.trackEvent(MIXPANEL_EVENT.MAX_SECURITY_INTEREST_REGISTERED, props);
  },

  groupFeaturesDiscovered(props: GroupFeaturesDiscoveredEventProps) {
    this.trackEvent(MIXPANEL_EVENT.GROUP_FEATURES_DISCOVERED, props);
  },

  memberActivityFilterUpdated(props: MemberActivityFilterUpdatedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.MEMBER_ACTIVITY_FILTER_UPDATED, props);
  },

  enterpriseReportPopupOpened(props: EnterpriseReportPopupOpenedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.ENTERPRISE_REPORT_POPUP_OPENED, props);
  },

  enterpriseReportGenerateClicked(
    props: EnterpriseReportGenerateClickedEventProps,
  ) {
    this.trackEvent(MIXPANEL_EVENT.ENTERPRISE_REPORT_GENERATE_CLICKED, props);
  },

  chatAttachmentToastShown(props: ChatAttachmentToastEventProps) {
    this.trackEvent(MIXPANEL_EVENT.CHAT_ATTACHMENT_TOAST_SHOWN, props);
  },

  chatAttachmentToastSaveClicked(props: ChatAttachmentToastEventProps) {
    this.trackEvent(MIXPANEL_EVENT.CHAT_ATTACHMENT_TOAST_SAVE_CLICKED, props);
  },

  chatAttachmentManageAccessClicked(props: ChatAttachmentToastEventProps) {
    this.trackEvent(
      MIXPANEL_EVENT.CHAT_ATTACHMENT_MANAGE_ACCESS_CLICKED,
      props,
    );
  },

  listingDocumentUploaded(props: ListingDocumentUploadedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.LISTING_DOCUMENT_UPLOADED, props);
  },

  aiDraftedFieldEdited(props: AiDraftedFieldEditedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.AI_DRAFTED_FIELD_EDITED, props);
  },

  automationStageOpened(props: AutomationStageOpenedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.AUTOMATION_STAGE_OPENED, props);
  },

  automationTourStarted(props: AutomationTourStartedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.AUTOMATION_TOUR_STARTED, props);
  },

  automationTourStepViewed(props: AutomationTourStepViewedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.AUTOMATION_TOUR_STEP_VIEWED, props);
  },

  automationTourCompleted(props: AutomationTourCompletedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.AUTOMATION_TOUR_COMPLETED, props);
  },

  automationTourClosed(props: AutomationTourClosedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.AUTOMATION_TOUR_CLOSED, props);
  },

  pausedLeadUnlocked(props: PausedLeadUnlockedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.PAUSED_LEAD_UNLOCKED, props);
  },

  pausedLeadReplyOpened(props: PausedLeadReplyOpenedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.PAUSED_LEAD_REPLY_OPENED, props);
  },

  automationRemovalCancelled(props: AutomationRemovalCancelledEventProps) {
    this.trackEvent(MIXPANEL_EVENT.AUTOMATION_REMOVAL_CANCELLED, props);
  },

  drawerRowNavigated(props: DrawerRowNavigatedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.DRAWER_ROW_NAVIGATED, props);
  },
};
